import { EdiAddress, EdiEntity, EdiPaymentLine, EdiClaimLine } from "./types";

// ===== Shared utility functions used across the EDI viewer components =====

export const formatCurrency = (amount?: number): string => {
  if (amount === undefined || amount === null) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

export const formatAddress = (addr?: EdiAddress): string => {
  if (!addr) return "-";
  const parts = [addr.line, addr.city, addr.stateCode, addr.zipCode].filter(
    Boolean,
  );
  return parts.length > 0 ? parts.join(", ") : "-";
};

export const formatEntityName = (entity?: EdiEntity): string => {
  if (!entity) return "-";
  const parts = [
    entity.firstName,
    entity.middleName,
    entity.lastNameOrOrgName,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "-";
};

export const formatPhone = (phone?: string): string => {
  if (!phone) return "-";
  if (phone.length === 10) {
    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
  }
  return phone;
};

export const extractProviderByRole = (
  providers: EdiEntity[] | undefined,
  role: string,
): EdiEntity | undefined => {
  if (!providers || providers.length === 0) return undefined;
  return providers.find((p) => p.entityRole === role);
};

export const getAllowedFromSupplemental = (
  line: EdiPaymentLine,
): number | undefined => {
  const allowed = line.supplementalAmounts?.find(
    (a) => a.qualifierCode === "B6",
  );
  return allowed?.amount;
};

/** Extract modifier codes from a service line. Handles line.modifiers (string[]), procedure.modifiers ({code}[]), procedureModifier (string[]). */
export const getModifiersFromLine = (
  line: EdiClaimLine | EdiPaymentLine,
): string[] => {
  // line.modifiers as string array (837)
  const mods = (line as EdiClaimLine).modifiers;
  if (mods && Array.isArray(mods) && mods.length > 0) {
    return mods.map((m) =>
      typeof m === "string" ? m : (m as { code?: string })?.code || String(m),
    );
  }
  // procedure.modifiers as array of {code, subType} (835)
  const procMods = line.procedure?.modifiers;
  if (procMods && Array.isArray(procMods) && procMods.length > 0) {
    return procMods
      .map((m) => (typeof m === "string" ? m : m?.code || ""))
      .filter(Boolean);
  }
  // procedureModifier (alternative EDI field name)
  const procMod = (line as { procedureModifier?: string[] }).procedureModifier;
  if (procMod && Array.isArray(procMod) && procMod.length > 0) {
    return procMod.map((m) => String(m));
  }
  return [];
};

export const getLinePatientResponsibility = (
  line: EdiPaymentLine,
): number | undefined => {
  if (!line.adjustments?.length) return undefined;
  const prAdjs = line.adjustments.filter(
    (a) => a.group === "PATIENT_RESPONSIBILITY",
  );
  if (prAdjs.length === 0) return undefined;
  return prAdjs.reduce((sum, a) => sum + (a.amount || 0), 0);
};

/** 837/835 transaction type: institutional shows facility type, professional/dental shows POS. Unknown/other shows both. */
export const facilityTypeVsPlaceOfServiceVisibility = (
  transactionType?: string,
) => {
  const isInstitutional = transactionType === "INST";
  const isProfessional =
    transactionType === "PROF" || transactionType === "DENT";
  const showBoth = !isInstitutional && !isProfessional;
  return {
    showFacilityType: isInstitutional || showBoth,
    showPlaceOfService: isProfessional || showBoth,
    facilityLabel: isInstitutional ? "Facility Type" : "Facility Code",
  };
};
